// The pitch fader (P3.6) — FR-05.
//
// "±8% default (±16%, ±50% in settings) with fine nudge", and vinyl-style: **the rate changes and
// the pitch follows it**, because keylock is FR-06 — **deferred to FF-3 at P3.11, so this is what
// v1 ships, not a placeholder.** That is not a limitation to apologise for: it is what every DJ did
// until 2005, and the roadmap's own fallback names it.
//
// The cost is exactly ±1.44 / +1.33 semitones at the default range (12·log₂(rate), asymmetric
// because the fader is linear in rate and pitch is not). See `ADR-LOG.md`, "SP-1 ruling", for the
// full table and the conditions that would reopen keylock.
//
// ## Why this is a `<input type="range">` and not a custom drag surface
//
// A pitch fader is the most tactile control on a deck and this is the least tactile widget on the
// web, which looks like the wrong trade until you list what it buys: keyboard operation (arrow keys
// move it, and UX §7 asks for operability), a pointer capture that survives the cursor leaving the
// element mid-drag, touch for free, and an accessibility tree that already knows what a slider is.
// A custom surface would have to earn all four back before it started being nicer to use.
//
// It is horizontal because a vertical `input[type=range]` needs `writing-mode` gymnastics that
// UX §5.1's token set has nothing to say about yet. Orientation is a visual-pass decision, not this
// prompt's.
//
// ## What streams, and what does not
//
// Every `input` event publishes a `ControlEvent` — that is the point of the prompt, and it is why
// `onChange` is not enough: `change` fires when a drag *ends*, and a fader that only spoke at the
// end would be a fader you cannot beatmatch with. The nudge buttons publish on press and release
// and change nothing directly; see `control-mapping.ts` on why a bend is not a second fader.

import { useRef, type JSX } from 'react';

import { PITCH_RANGES, ratePercent } from '../../audio/engine';
import type { TrackLoaderApi } from './useTrackLoader';

export interface PitchFaderProps {
  readonly loader: TrackLoaderApi;
}

/** `+0.00%` — signed, and to two decimals because ±8% across a fader is a range where the second
 * decimal is the difference between matched and drifting over four minutes.
 *
 * ⚠️ **The sign is taken from the rounded value, not the raw one, and the difference is the bug the
 * stakeholder saw as "PITCH+0.00%" (4 Sep).** A rate of 1.00000004 is +0.000004%, which is positive
 * — so the sign came out `+` — and rounds to `0.00`, so the digits came out zero. Sign from one
 * number and digits from another is the signed-zero family, and it reads as a control that cannot
 * return to centre. Rounding first means the two agree by construction: anything that *displays* as
 * zero *is* zero as far as this readout is concerned, and gets no sign. */
export function formatPitchPercent(rate: number): string {
  const percent = ratePercent(rate);
  const rounded = Math.round(percent * 100) / 100;
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${Math.abs(rounded).toFixed(2)}%`;
}

export function PitchFader({ loader }: PitchFaderProps): JSX.Element {
  const faderRef = useRef<HTMLInputElement | null>(null);
  // Deck B renders this same component (P3.7). The id has to be unique per deck — two elements
  // sharing one `id` breaks `htmlFor` on both, so the label and the readout would point at deck A's
  // fader from inside deck B's panel — and every accessible name has to say which deck it is, since
  // "Pitch" twice on one screen is two controls a screen reader cannot tell apart.
  const deck = loader.deckId.toUpperCase();
  const faderId = `deck-${loader.deckId}-pitch`;

  return (
    <div className="stack stack--tight">
      <div className="pitch">
        <label className="micro" htmlFor={faderId}>
          Pitch
        </label>
        <input
          id={faderId}
          ref={faderRef}
          type="range"
          min={0}
          max={1}
          // Ten thousand positions across the travel. At ±8% that is a rate step of 1.6e-5 — some
          // three orders of magnitude below anything audible — so the fader's resolution is the
          // pointer's, not the control's.
          step={0.0001}
          value={loader.pitchFader}
          onChange={(event) => {
            loader.setPitchFader(event.currentTarget.valueAsNumber);
          }}
          aria-label={`Deck ${deck} pitch`}
          aria-valuetext={formatPitchPercent(loader.pitchRate)}
        />
        <output className="mono pitch__readout" htmlFor={faderId}>
          {formatPitchPercent(loader.pitchRate)}
        </output>
      </div>

      <div className="row row--tight">
        {/* Held, not clicked. `pointerdown`/`pointerup` rather than `click`, because a bend that
            ended when the mouse button came up is the whole behaviour — a click has no duration.
            `onPointerLeave` releases too: dragging off a held button must not leave the deck
            permanently bent, which is the bug every hold-to-act control ships once. */}
        <button
          type="button"
          onPointerDown={() => {
            loader.nudge(-1, true);
          }}
          onPointerUp={() => {
            loader.nudge(-1, false);
          }}
          onPointerLeave={() => {
            loader.nudge(-1, false);
          }}
          aria-label={`Nudge deck ${deck} slower`}
        >
          −
        </button>
        <button
          type="button"
          onPointerDown={() => {
            loader.nudge(1, true);
          }}
          onPointerUp={() => {
            loader.nudge(1, false);
          }}
          onPointerLeave={() => {
            loader.nudge(1, false);
          }}
          aria-label={`Nudge deck ${deck} faster`}
        >
          +
        </button>

        <button
          type="button"
          onClick={() => {
            loader.setPitchFader(0.5);
          }}
          // Centre is the position a fader spends most of its life at and the one a pointer cannot
          // hit exactly. "Nearly 1.0" is a track that drifts out of sync over four minutes, so the
          // exact value needs a control of its own rather than a steady hand.
          aria-label={`Reset deck ${deck} pitch to zero`}
        >
          0
        </button>

        {/* FR-05's ranges. Three buttons rather than a select: they are a resolution choice a DJ
            makes mid-set, and a two-click dropdown is the wrong shape for that. */}
        {PITCH_RANGES.map((range) => (
          <button
            key={range}
            type="button"
            className={loader.pitchRange === range ? 'is-active' : undefined}
            aria-pressed={loader.pitchRange === range}
            onClick={() => {
              loader.setPitchRange(range);
            }}
          >
            ±{(range * 100).toFixed(0)}%
          </button>
        ))}
      </div>
    </div>
  );
}
